import Stack from '@mui/material/Stack';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';
import { validateProps } from '../../../utils/propValidator';
import {
  ControlledComboBox,
  ControlledImageUpload,
  ControlledSelect,
  ControlledTextField,
  ErrorSummary,
  FormFieldWrapper,
  ModalWrapper,
  SectionTitle,
} from '../../shared/components';
import { postWebsiteProject, updateWebsiteProject } from '../website.service';

const propRules = {
  open: { type: 'boolean', required: true },
  onClose: { type: 'function', required: true },
  mode: { oneOf: ['add', 'edit'] },
  onSubmit: { type: 'function', required: true },
  tags: { type: 'array', items: { type: 'string' } },
  locations: { type: 'array', items: { type: 'string' } },
  project: { type: ['object', 'null'] },
};

const PROJECT_TYPES = ['Residential', 'Commercial', 'Institutional', 'Industrial', 'Other'];

const DEFAULT_VALUES = {
  title: '',
  location: '',
  completionYear: null,
  description: '',
  tags: [],
  photos: [],
  projectType: '',
  area: '',
  builderName: '',
  builderContact: '',
  inProgressStatus: '',
  status: '',
};

export default function WebsiteProjectEntryForm(props) {
  validateProps(props as Record<string, unknown>, propRules, 'WebsiteProjectEntryForm');
  const { open, onClose, mode, onSubmit } = props;

  const [saving, setSaving] = useState(false);

  const methods = useForm({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  });

  const { handleSubmit, watch, control, reset } = methods;

  useEffect(() => {
    if (!open) return;

    if (mode === 'edit' && props.project) {
      reset({
        ...DEFAULT_VALUES,
        ...props.project,
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [open, mode, props.project, reset]);

  const statusWatch = watch('status');

  const handleProjectSubmit = async (data) => {
    setSaving(true);
    try {
      if (mode === 'edit') {
        const projectId = props.project.id;
        if (!projectId) return;
        await updateWebsiteProject(projectId, data);
        toast.success('Project updated successfully');
      } else if (mode === 'add') {
        await postWebsiteProject(data);
        toast.success('Project updated successfully');
      }
      onSubmit();
    } catch (err) {
      toast.error(
        logError({
          message: 'Failed to publish project to website',
          error: err,
          code: 'WEBSITE_ACTION_FAILED',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalWrapper open={open} onClose={() => (saving ? {} : onClose())} maxWidth='md'>
      <FormProvider {...methods}>
        <Stack
          component='form'
          onSubmit={handleSubmit(handleProjectSubmit)}
          direction='column'
          gap={3}
        >
          <SectionTitle title={mode === 'add' ? 'Add project' : 'Edit project'} />
          <ErrorSummary />

          <ControlledSelect
            id='projectType'
            name='projectType'
            label='Project type'
            options={PROJECT_TYPES.map((type) => ({ value: type, label: `${type} Project` }))}
            rules={{ required: 'Project type is required' }}
            fullWidth
          />

          <ControlledTextField
            id='title'
            name='title'
            label='Project name'
            rules={{
              required: 'Project name is required',
              minLength: { value: 3, message: 'Project name must be at least 3 characters long' },
              maxLength: { value: 60, message: 'Project name must be at most 60 characters long' },
            }}
            fullWidth
          />

          <ControlledComboBox
            id='location'
            name='location'
            label='Location'
            rules={{ required: 'Location is required' }}
            options={props.locations}
            placeholder='Select location'
            freeSolo
            fullWidth
          />

          <ControlledTextField
            id='builderName'
            name='builderName'
            label='Builder name'
            rules={{
              required: 'Builder name is required',
              minLength: { value: 3, message: 'Builder name must be at least 3 characters long' },
              maxLength: { value: 60, message: 'Builder name must be at most 60 characters long' },
            }}
            fullWidth
            placeholder='Enter name'
          />

          <ControlledTextField
            id='builderContact'
            name='builderContact'
            label='Builder contact number'
            type='tel'
            rules={{
              minLength: { value: 10, message: 'Contact number must be 10 characters long' },
              maxLength: { value: 10, message: 'Contact number must be 10 characters long' },
            }}
            fullWidth
            placeholder='Enter contact number'
          />

          <ControlledTextField
            id='area'
            name='area'
            label='Total area of project'
            type='number'
            rules={{
              required: 'Total area of project is required',
              min: { value: 1, message: 'Total area of project must be at least 1 sq.ft.' },
            }}
            fullWidth
            placeholder='Enter total area'
            suffix='sq.ft.'
          />

          <Stack direction='row' alignItems='center' gap={4} justifyContent='space-between'>
            <ControlledSelect
              id='status'
              name='status'
              label='Status'
              options={[
                { value: 'in_progress', label: 'In progress' },
                { value: 'completed', label: 'Completed' },
              ]}
              rules={{ required: 'Status is required' }}
              fullWidth
            />
            {statusWatch === 'in_progress' ? (
              <ControlledTextField
                id='inProgressStatus'
                name='inProgressStatus'
                label='Mention status'
                rules={{ required: 'Status is required' }}
                fullWidth
              />
            ) : (
              <Controller
                name='completionYear'
                control={control}
                render={({ field }) => (
                  <FormFieldWrapper label='Year of completion' htmlFor='completionYear'>
                    <DatePicker
                      disableFuture
                      maxDate={dayjs()}
                      yearsOrder='desc'
                      views={['year']}
                      slotProps={{
                        inputAdornment: { sx: { mr: 1.5 } },
                        popper: { sx: { '& .MuiDateCalendar-root': { height: 'auto' } } },
                        openPickerIcon: { sx: { width: 18, height: 18 } },
                        yearButton: {
                          sx: {
                            fontSize: '0.875rem',
                            borderRadius: '8px',
                            '&.Mui-selected': {
                              backgroundColor: (theme) =>
                                `${theme.palette.primary.light} !important`,
                            },
                          },
                        },
                      }}
                      value={field.value ? dayjs().year(field.value) : null}
                      onChange={(date) => field.onChange(date?.year() ?? null)}
                    />
                  </FormFieldWrapper>
                )}
              />
            )}
          </Stack>

          <ControlledTextField
            id='description'
            name='description'
            label='Description'
            rules={{
              required: 'Description is required',
              minLength: {
                value: 50,
                message: 'Description must be at least 50 characters long',
              },
              maxLength: {
                value: 1000,
                message: 'Description must be at most 1000 characters long',
              },
            }}
            placeholder='Enter description'
            isMultiline
            rows={4}
            showCharCount
            fullWidth
          />

          <ControlledComboBox
            id='tags'
            name='tags'
            label='Tags'
            rules={{ required: 'Tags are required' }}
            options={props.tags}
            placeholder='Add tags'
            multiple
            freeSolo
            fullWidth
            commitOnSpace
          />

          <div className='self-start'>
            <ControlledImageUpload
              id='photos'
              name='photos'
              rules={{ required: 'Project photos are required' }}
              buttonText='Upload photos'
              multiple
              previewAfter
              maxFileSize={5}
              aspectRatio='16:9'
              uploaderTitle='Upload project photos'
              viewerTitle='Project photos'
              minImages={2}
              maxImages={5}
            />
          </div>

          <button type='submit' className='btn cta-btn self-end' disabled={saving}>
            Publish project
          </button>
        </Stack>
      </FormProvider>
    </ModalWrapper>
  );
}
